import React, { useState } from "react";
import QurealSection from "../common/QurealSection";
import SectionHeader from "../components/commons/SectionHeader";
import { FileImageOutlined } from "@ant-design/icons";
import { Button, Checkbox, Segmented, Select, message } from "antd";
import styles from "./styles/AIImageGen.module.css";
import classNames from "classnames";
import { startCase } from "lodash";
import {
    ArrowRightOutlined,
    DownloadOutlined,
    CloseOutlined,
} from "@ant-design/icons"; //download button
import { useAIStudioPerson, useAIStudioRawImage } from "../_actions/bot";
import log from "cslog";
import PersonStudioInputs from "./PersonStudioInputs";
import RawStudioInputs from "./RawStudioInputs";
import { mapPersonConfigToPayload } from "./mapPersonConfig";

const DEFAULT_CONFIG = {
    size: "portrait",
    gender: "female",
};
const AP_MAPPING = {
    square: "1:1",
    portrait: "3:4",
    portrait_long: "9:16",
    landscape: "16:9",
    landscape_long: "16:9",
};

function AIImageGen() {
    const [studio_type, setStudioType] = useState("person");
    const [result, setResult] = useState(null);
    const [image_loading, setImageLoading] = useState(false);
    const mutationGen = useAIStudioPerson((result) => {
        log.d(result, "result in useAIStudioPerson");
        if (result?.success) {
            setResult(result);
        }
    });
    //new for rawimages
    const mutationRaw = useAIStudioRawImage((result) => {
        console.log("RAW success handler:", result);
        if (!result?.success) {
            message.error(
                result?.error?.includes("RESOURCE_EXHAUSTED")
                    ? "Image generation limit reached. Try again later."
                    : "Image generation failed."
            );
            setImageLoading(false);
            return;
        }
        const file = result.data;
        setResult({
            success: true,
            data: [
                {
                    url: `https://api.qureal.com/assets/${file.filename_disk}`,
                },
            ],
        });
        setImageLoading(false);
    });
    const [config, setConfig] = useState(DEFAULT_CONFIG);

    //download image
    const downloadImage = async (url, filename = "generated-image.png") => {
        try {
            const response = await fetch(url, { mode: "cors" });
            const blob = await response.blob();

            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed", err);
        }
    };

    return (
        <QurealSection
            header={
                <SectionHeader
                    heading="AI Image Studio"
                    icon={<FileImageOutlined />}
                />
            }
            footer={false} //no footer suggested by mahi
        >
            <div className={styles.wrapper}>
                <div className={styles.sidebar}>
                    <Select
                        value={studio_type}
                        onChange={(value) => {
                            setStudioType(value);
                        }}
                        style={{ width: "100%" }}
                        options={[
                            { label: "Person", value: "person" },
                            { label: "Raw", value: "raw" },
                        ]}
                    />

                    {studio_type === "raw" && (
                        <RawStudioInputs
                            config={config}
                            setConfig={setConfig}
                        />
                    )}
                    {studio_type === "person" && (
                        <PersonStudioInputs
                            config={config}
                            setConfig={setConfig}
                        />
                    )}

                    <div
                        className={styles.bottom_panel}
                        // style={{ marginTop: "1rem", display: "flex", gap: "10px", justifyContent: "flex-end" }}
                    >
                        <Button onClick={() => setConfig(DEFAULT_CONFIG)}>
                            Reset
                        </Button>
                        <Button
                            type="primary"
                            icon={<ArrowRightOutlined />}
                            size="middle"
                            iconPosition="end"
                            onClick={() => {
                                console.log("Generate clicked");
                                console.log("Studio type:", studio_type);
                                console.log("Config at click:", config);

                                setResult(null);
                                setImageLoading(true);

                                if (studio_type === "raw") {
                                    const payload = {
                                        image_style: config.image_style,
                                        user_input: config.prompt,
                                        model_name: "gemini-2.5-flash-image",
                                        negative_space: config.negative_space,
                                        object_position: config.object_position,
                                        aspect_ratio: AP_MAPPING[config.size],
                                        image_style: config.image_style,
                                    };

                                    console.log("RAW payload:", payload);
                                    console.log(
                                        "Aspect ratio being sent:",
                                        AP_MAPPING[config.size]
                                    );

                                    mutationRaw.mutate(payload);
                                } else {
                                    const payload =
                                        mapPersonConfigToPayload(config);

                                    console.log(
                                        "PERSON payload (normalized):",
                                        payload
                                    );

                                    mutationGen.mutate(payload);
                                }
                            }}
                            loading={
                                studio_type === "raw"
                                    ? mutationRaw.isLoading
                                    : mutationGen.isLoading
                            }
                        >
                            Generate
                        </Button>
                    </div>
                </div>
                <div className={styles.content}>
                    {((studio_type === "raw"
                        ? mutationRaw.isLoading
                        : mutationGen.isLoading) ||
                        image_loading) && (
                        <div
                            className={styles.image_placeholder}
                            style={{
                                aspectRatio: AP_MAPPING[config.size],
                            }}
                        ></div>
                    )}
                    {/* {!result?.success && <pre>{JSON.stringify(config, null, 2)}</pre>} */}
                    {result?.success && result?.data?.length > 0 && (
                        <div className={styles.image_wrapper}>
                            <div className={styles.image_stage}>
                                <div
                                    className={styles.image_box}
                                    style={{
                                        "--ar": AP_MAPPING[config.size],
                                    }}
                                >
                                    <img
                                        src={`${
                                            result.data[0].url
                                        }?t=${Date.now()}`}
                                        alt="Generated Image"
                                        onLoad={() => setImageLoading(false)}
                                    />
                                </div>

                                {/* ACTIONS FLOAT ABOVE – NEVER CLIPPED */}
                                <div className={styles.image_actions}>
                                    <CloseOutlined
                                        className={styles.cut_icon}
                                        title="Discard image"
                                        onClick={() => setResult(null)}
                                    />
                                    <DownloadOutlined
                                        className={styles.download_icon}
                                        title="Download image"
                                        onClick={() =>
                                            downloadImage(
                                                result.data[0].url,
                                                `ai-image-${Date.now()}.png`
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {/* {result?.success && <pre className={styles.prompt_view}>{result?.prompt}</pre>} */}
                </div>
            </div>
        </QurealSection>
    );
}

export default AIImageGen;
